import { useEffect, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { fetchBooking, cancelBooking, reinstateBooking, deleteBooking } from "~/apis/bookings"
import { fetchCleaners } from "~/apis/cleaners"
import { createClean, transitionClean, deleteClean } from "~/apis/cleans"
import { dismissGuestReminder, snoozeGuestReminder } from "~/apis/guestReminders"
import { Input } from "@/components/ui/input"
import { PageSpinner } from "~/components/Spinner"
import CleanKindFields, { CLEAN_KIND_LABELS, addDays } from "~/components/CleanKindFields"
import CleanEditor from "~/components/CleanEditor"
import routes from "~/routes"

function formatDate(iso) {
  return new Date(`${iso}T00:00:00`).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short", year: "numeric" })
}

const CLEAN_STATUS_LABELS = { pending: "waiting on cleaner", accepted: "confirmed", done: "done", declined: "declined", cancelled: "cancelled" }

const REMINDER_LABELS = {
  checkin_instructions: "Check-in info (Riparide)",
  welcome: "Welcome message",
  checkout: "Check-out note",
}

function ScheduleCleanForm({ booking, onScheduled }) {
  const [ cleaners, setCleaners ] = useState([])
  const [ cleanerId, setCleanerId ] = useState("")
  const following = booking.following_booking
  const [ form, setForm ] = useState({
    kind: "scheduled",
    window_starts_on: booking.ends_on,
    // For "cleaner to confirm": up to the day before the next guests, else a week.
    window_ends_on: following ? addDays(following.starts_on, -1) : addDays(booking.ends_on, 7),
    scheduled_time: "",
  })
  const [ status, setStatus ] = useState("accepted")
  const [ instructions, setInstructions ] = useState("")
  const [ timingNotes, setTimingNotes ] = useState("")
  const [ error, setError ] = useState(null)
  const [ busy, setBusy ] = useState(false)

  useEffect(() => {
    // Cleaners work one property each, so only this property's cleaners are offered.
    fetchCleaners().then((all) => setCleaners(all.filter((c) => c.active && c.property_id === booking.property_id)))
  }, [ booking.property_id ])

  async function submit(event) {
    event.preventDefault()
    if (busy) return
    setBusy(true)
    setError(null)

    try {
      await createClean({
        bookingId: booking.id,
        cleanerId: cleanerId || null,
        kind: form.kind,
        status,
        windowStartsOn: form.window_starts_on || booking.ends_on,
        windowEndsOn: form.kind === "cleaner_chooses" ? form.window_ends_on : null,
        scheduledTime: form.kind === "scheduled" ? form.scheduled_time : null,
        instructions,
        timingNotes,
      })
      onScheduled()
    } catch (err) {
      setError(err.message)
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} className="mt-3 space-y-3 rounded-lg border border-border bg-muted/50 p-3">
      {error && <p className="text-sm text-destructive">{error}</p>}

      <p className="text-sm">
        Guests check out {formatDate(booking.ends_on)}.{" "}
        {following ? (
          <>
            Next stay: <span className="font-medium">{following.guest_label}</span> arrives {formatDate(following.starts_on)}
            {following.gap_nights === 0 ? (
              <Badge className="ml-2 bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300">same day — back-to-back</Badge>
            ) : (
              <span className="text-muted-foreground"> — {following.gap_nights} night{following.gap_nights === 1 ? "" : "s"} free after checkout</span>
            )}
          </>
        ) : (
          <span className="text-muted-foreground">No later booking yet.</span>
        )}
      </p>

      <CleanKindFields form={form} onChange={setForm} />

      <div className="grid grid-cols-2 gap-2">
        <Select value={cleanerId || "unassigned"} onValueChange={(value) => setCleanerId(value === "unassigned" ? "" : value)}>
          <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="unassigned">{cleaners.length === 0 ? "No cleaner for this property yet" : "Unassigned"}</SelectItem>
            {cleaners.map((cleaner) => (
              <SelectItem key={cleaner.id} value={String(cleaner.id)}>{cleaner.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="accepted">Confirmed with the cleaner</SelectItem>
            <SelectItem value="pending">Still to confirm — ask them</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <input type="text" value={timingNotes} onChange={(event) => setTimingNotes(event.target.value)} placeholder="Timing notes — what's agreed or still to confirm (the cleaner sees this)" className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm" />
      <input type="text" value={instructions} onChange={(event) => setInstructions(event.target.value)} placeholder="Instructions for the cleaner (optional)" className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm" />

      <Button type="submit" size="sm" disabled={busy}>{busy ? "Saving…" : "Save clean"}</Button>
    </form>
  )
}

export default function BookingDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [ booking, setBooking ] = useState(null)
  const [ error, setError ] = useState(null)
  const [ showSchedule, setShowSchedule ] = useState(false)
  const [ editingClean, setEditingClean ] = useState(false)
  const [ cleanError, setCleanError ] = useState(null)
  const [ propertyCleaners, setPropertyCleaners ] = useState([])

  useEffect(() => {
    if (!booking) return
    fetchCleaners().then((all) => setPropertyCleaners(all.filter((c) => c.active && c.property_id === booking.property_id))).catch(() => {})
  }, [ booking?.property_id ])

  async function runClean(action) {
    setCleanError(null)
    try {
      await action()
      load()
    } catch (err) {
      setCleanError(err.message)
    }
  }

  async function runReminder(action) {
    try {
      await action()
      load()
    } catch (err) {
      setError(err.message)
    }
  }
  const [ confirmAction, setConfirmAction ] = useState(null) // "cancel" | "delete" | null

  useEffect(() => {
    load()
  }, [ id ])

  function load() {
    fetchBooking(id).then(setBooking).catch((err) => setError(err.message))
  }

  async function handleCancel() {
    await cancelBooking(id)
    setConfirmAction(null)
    load()
  }

  async function handleDelete() {
    await deleteBooking(id)
    setConfirmAction(null)
    navigate(routes.home)
  }

  if (error) {
    return (
      <div className="mx-auto max-w-lg px-6 py-12">
        <Link to={routes.home} className="mb-3 inline-block text-sm text-muted-foreground">← Back to calendar</Link>
        <p className="text-destructive">Couldn't load this booking: {error}</p>
      </div>
    )
  }
  if (!booking) return <PageSpinner />

  const isBlock = booking.kind === "block"
  // One clean per booking: the live one, if any (cancelled ones don't count).
  const activeClean = (booking.cleans || []).find((c) => c.status !== "cancelled")

  return (
    <div className="mx-auto max-w-lg px-6 py-12">
      <Link to={routes.home} className="mb-3 inline-block text-sm text-muted-foreground">← Back to calendar</Link>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-lg">{isBlock ? (booking.notes ? `Blocked · ${booking.notes}` : "Blocked dates") : booking.guest_label}</CardTitle>
              <p className="text-sm text-muted-foreground">
                {booking.property_name} · {formatDate(booking.starts_on)} – {formatDate(booking.ends_on)} · {booking.nights} night{booking.nights === 1 ? "" : "s"}
              </p>
            </div>
            <div className="flex flex-col items-end gap-1">
              <Badge variant={booking.status === "cancelled" ? "destructive" : "secondary"}>{booking.status}</Badge>
              {booking.kind === "residency" ? (
                <Badge className="bg-brand/15 text-brand">residency</Badge>
              ) : (
                <Badge variant="outline">{booking.source}</Badge>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {booking.needs_enrichment && (
            <p className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200">
              ⚠ Guest details missing — add a name{booking.source !== "airbnb" && " and phone number so messages work"}.
            </p>
          )}

          <dl className="space-y-1 text-sm">
            {!isBlock && (
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Guest</dt>
                <dd className="text-foreground">{booking.guest_label}</dd>
              </div>
            )}
            {booking.guest_phone && (
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Phone</dt>
                <dd className="text-foreground">{booking.guest_phone}</dd>
              </div>
            )}
            {booking.guest_email && (
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Email</dt>
                <dd className="text-foreground">{booking.guest_email}</dd>
              </div>
            )}
            {booking.guest_count && (
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Guests</dt>
                <dd className="text-foreground">{booking.guest_count}</dd>
              </div>
            )}
            {(booking.flag_definitions || []).filter((definition) => booking.flags?.[definition.key]).map((definition) => (
              <div key={definition.key} className="flex justify-between">
                <dt className="text-muted-foreground">For the cleaner</dt>
                <dd className="text-foreground">{definition.label}</dd>
              </div>
            ))}
          </dl>

          {booking.notes && <p className="mt-3 rounded-lg border border-border p-3 text-sm text-foreground">{booking.notes}</p>}

          {booking.feed_origin && (booking.ical_summary || booking.ical_description) && (
            <details className="mt-3 text-sm">
              <summary className="cursor-pointer text-muted-foreground">Raw feed info</summary>
              <p className="mt-1 font-medium text-foreground">{booking.ical_summary}</p>
              {booking.ical_description && <pre className="mt-1 whitespace-pre-wrap font-sans text-muted-foreground">{booking.ical_description}</pre>}
            </details>
          )}

          <div className="mt-4 flex flex-wrap gap-2">
            <Button asChild variant="outline" size="sm">
              <Link to={routes.editBooking(booking.id)}>{isBlock ? "Edit / add details" : booking.synced ? "Edit guest details" : "Edit"}</Link>
            </Button>
            {!booking.synced && booking.status !== "cancelled" && (
              <Button variant="outline" size="sm" onClick={() => setConfirmAction("cancel")}>{isBlock ? "Remove block" : "Cancel booking"}</Button>
            )}
            {booking.status === "cancelled" && (
              <Button variant="outline" size="sm" onClick={async () => { await reinstateBooking(booking.id); load() }}>Reinstate booking</Button>
            )}
            {!booking.synced && (
              <Button variant="outline" size="sm" className="text-destructive" onClick={() => setConfirmAction("delete")}>Delete</Button>
            )}
          </div>

          {!isBlock && <section className="mt-6">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Turnover clean</Label>
            {!activeClean && <p className="mt-1 text-sm text-muted-foreground">No clean scheduled yet.</p>}
            {activeClean && (
              <div className="mt-1 overflow-hidden rounded-lg border border-border text-sm">
                <div className="flex items-center justify-between gap-3 border-b border-border bg-muted/40 px-3 py-2">
                  <p className="font-medium text-foreground">
                    {activeClean.cleaner_name || "No cleaner yet"}
                    <span className="font-normal text-muted-foreground"> · {CLEAN_KIND_LABELS[activeClean.kind]}</span>
                  </p>
                  <Badge variant={activeClean.flagged_reason ? "destructive" : "secondary"} className="shrink-0">
                    {activeClean.flagged_reason ? "flagged" : CLEAN_STATUS_LABELS[activeClean.status] || activeClean.status}
                  </Badge>
                </div>
                <div className="space-y-1 px-3 py-2.5">
                  <p className="text-foreground">{activeClean.when_label}</p>
                  {activeClean.timing_notes && <p className="text-muted-foreground">⏰ {activeClean.timing_notes}</p>}
                  {activeClean.instructions && <p className="text-muted-foreground">Instructions: {activeClean.instructions}</p>}
                  {activeClean.flagged_reason && <p className="text-destructive">⚠ {activeClean.flagged_reason}</p>}
                  {cleanError && <p className="text-destructive">{cleanError}</p>}
                </div>
                {editingClean ? (
                  <div className="px-3 pb-3">
                    <CleanEditor clean={activeClean} cleaners={propertyCleaners} onSaved={() => { setEditingClean(false); load() }} onCancel={() => setEditingClean(false)} />
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center gap-2 border-t border-border px-3 py-2">
                    <Button size="sm" variant="outline" onClick={() => setEditingClean(true)}>Edit</Button>
                    {activeClean.status === "pending" && (
                      <Button size="sm" variant="outline" onClick={() => runClean(() => transitionClean(activeClean.id, "confirm"))}>Cleaner confirmed</Button>
                    )}
                    {activeClean.status === "accepted" && (
                      <Button size="sm" variant="outline" onClick={() => runClean(() => transitionClean(activeClean.id, "mark_done"))}>Mark done</Button>
                    )}
                    <span className="ml-auto flex items-center gap-3 text-xs">
                      {activeClean.status !== "done" && (
                        <button type="button" className="text-muted-foreground hover:underline" onClick={() => runClean(() => transitionClean(activeClean.id, "cancel"))}>Cancel clean</button>
                      )}
                      <button type="button" className="text-destructive hover:underline" onClick={() => { if (window.confirm("Delete this clean entirely?")) runClean(() => deleteClean(activeClean.id)) }}>Delete</button>
                      <Link to={`${routes.cleans}?highlight=${activeClean.id}`} className="text-muted-foreground hover:underline">Cleans page →</Link>
                    </span>
                  </div>
                )}
              </div>
            )}
            {!activeClean && (!showSchedule ? (
              <Button size="sm" variant="outline" className="mt-2" onClick={() => setShowSchedule(true)}>+ Schedule a clean</Button>
            ) : (
              <ScheduleCleanForm booking={booking} onScheduled={() => { setShowSchedule(false); load() }} />
            ))}
          </section>}

          {booking.guest_reminders?.length > 0 && (
            <section className="mt-6">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Guest messages</Label>
              <ul className="mt-1 space-y-1">
                {booking.guest_reminders.map((reminder) => (
                  <li key={reminder.id} className="flex flex-wrap items-center justify-between gap-1 text-sm">
                    <span className="text-foreground">
                      {REMINDER_LABELS[reminder.kind]} — {reminder.status}
                      {reminder.status === "pending" && reminder.due_label && <span className="text-muted-foreground"> · due {reminder.due_label}</span>}
                    </span>
                    {reminder.status !== "done" && reminder.status !== "dismissed" && (
                      <span className="flex gap-1">
                        <Button asChild size="sm" variant="ghost">
                          <Link to={routes.sendMessage(booking.id) + `?kind=${reminder.kind}`}>Send</Link>
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => runReminder(() => snoozeGuestReminder(reminder.id))}>Tomorrow</Button>
                        <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => runReminder(() => dismissGuestReminder(reminder.id))}>Dismiss</Button>
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {booking.message_logs?.length > 0 && (
            <section className="mt-6">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Message history</Label>
              <ul className="mt-1 space-y-2">
                {booking.message_logs.map((log) => (
                  <li key={log.id} className="text-sm">
                    <p className="text-muted-foreground">{REMINDER_LABELS[log.kind] || log.kind} · {new Date(log.sent_at).toLocaleString("en-AU")}</p>
                    <p className="text-foreground">{log.body}</p>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={confirmAction !== null} onOpenChange={(open) => !open && setConfirmAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction === "delete" ? "Delete this booking entirely?" : isBlock ? "Remove this block?" : "Cancel this booking?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction === "delete"
                ? "This can't be undone."
                : isBlock
                  ? "The property goes back on the calendar as available for these dates."
                  : "Its reminders will be dismissed and any cleans flagged."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Never mind</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={confirmAction === "delete" ? handleDelete : handleCancel}
            >
              {confirmAction === "delete" ? "Delete" : isBlock ? "Remove block" : "Cancel booking"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
